import { ChevronDown, ChevronUp } from "lucide-react";
import type React from "react";
import { useState } from "react";
import Seo from "../components/Seo";

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "HOW DO I BOOK A CLASS?",
      answer:
        "You can book a class using our free CYCLE CO Saskatoon App for iOS and Android or through our website at www.cyclecollective.ca",
    },
    {
      question: "HOW FAR IN ADVANCE CAN I BOOK INTO A CLASS?",
      answer:
        "Booking will go live for all classes 7 days before the scheduled class time.",
    },
    {
      question: "DO YOU OFFER SPIN SHOES?",
      answer:
        "Yes! At Cycle Co we offer free shoe rental for each client. You are welcome to use your own spin shoes as long as they are SPD compatible.",
    },
    {
      question: "HOW EARLY SHOULD I ARRIVE TO CLASS?",
      answer:
        "If you are new to indoor cycling and have not attended CYCLE CO BASICS please arrive 15 minutes early so a team member can help with shoe sizing, bike set-up and studio orientation. Otherwise, please arrive 5-10 minutes early and be prepared to start class 2 minutes before the scheduled time.",
    },
    {
      question: "WHAT IS THE CANCELLATION POLICY?",
      answer:
        "Cancelation policy is 8hrs. If canceled less then 8 hours before class it’s $15. No show clients will be charged $15 & lose an associated credit should they be utilizing credit passes.",
    },
    {
      question: "DO YOU HAVE CHANGE ROOMS AND SHOWERS?",
      answer:
        "Yes, our bathrooms and showers are always stocked with luxury products and fresh towels. We also have lockers in the hallway for storage of personal items.",
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div
      className="min-h-full"
      style={{ backgroundColor: "var(--color-complementary)" }}
    >
      <Seo
        title="FAQ"
        description="Answers to common questions about Cycle Collective Saskatoon: booking classes, spin shoes, arrival times, cancellation policy, showers, and more."
        path="/experience/faq"
      />
      <script
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD structured data
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: "var(--color-text)" }}
            >
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-black/70 max-w-2xl mx-auto">
              Everything you need to know about joining Cycle Collective
              Saskatoon
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white/50 backdrop-blur-sm rounded-lg shadow-sm border border-white/20 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/30 transition-colors"
                >
                  <h3
                    className="text-lg font-semibold pr-4"
                    style={{ color: "var(--color-text)" }}
                  >
                    {faq.question}
                  </h3>
                  {openIndex === index ? (
                    <ChevronUp
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: "var(--color-text)" }}
                    />
                  ) : (
                    <ChevronDown
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: "var(--color-text)" }}
                    />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-4">
                    <p className="text-black/80 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="text-center mt-12">
            <div className="bg-white/30 backdrop-blur-sm rounded-lg p-8 max-w-2xl mx-auto">
              <h3
                className="text-xl font-semibold mb-3"
                style={{ color: "var(--color-text)" }}
              >
                Still have questions?
              </h3>
              <p className="text-black/70 mb-4">
                We're here to help! Reach out to our team for personalized
                assistance.
              </p>
              <a
                href="/contact"
                className="inline-block px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  backgroundColor: "var(--color-altBrand)",
                  color: "var(--color-text)",
                }}
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
